import { LOGOUT } from '../actions/screen';
import {
    ENQUEUE_MESSENGER_MESSAGE,
    FAIL_MESSENGER_MESSAGE,
    MARK_MESSENGER_CHAT_READ,
    RESOLVE_MESSENGER_MESSAGE,
    SET_MESSENGER_ACTIVE_CHAT,
    SET_MESSENGER_SEARCH,
    SET_MESSENGER_FLIGHT_SCOPE,
    SET_MESSENGER_STATUS,
    UPDATE_MESSENGER_CHATS,
    UPDATE_MESSENGER_RECIPIENTS,
    UPDATE_MESSENGER_THREAD,
} from '../actions/messenger';

const initialState = {
    chats: [],
    messagesByChat: {},
    threadSyncTime: {},
    outbox: [],
    recipients: [],
    activeChat: null,
    flightScopePlanFlightId: null,
    search: '',
    status: 'idle',
    syncTime: undefined,
};

const chatKey = (chat) => `${chat.type}_${chat.id}`;

const upsertById = (list, items) => {
    const byId = new Map(list.map((item) => [`${item.type}_${item.id}`, item]));

    items.forEach((item) => {
        const key = `${item.type}_${item.id}`;
        byId.set(key, { ...byId.get(key), ...item });
    });

    return Array.from(byId.values()).sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));
};

/*
 * Бэкенд отдаёт ПОЛНУЮ ветку диалога/темы → сервер является источником истины.
 * Поэтому: серверный набор сообщений замещает прежний (сообщения, исчезнувшие на
 * сервере, пропадают и из ленты — без «фантомов»), но при совпадении id сохраняем
 * локальные поля (status доставки, локальные превью вложений), а также оставляем
 * ещё не отправленные оптимистичные сообщения (без id) до их подтверждения.
 */
const mergeMessages = (existing = [], incoming = []) => {
    const prevById = new Map(existing.filter((m) => m.id != null).map((m) => [m.id, m]));
    const pending = existing.filter((m) => m.id == null);

    const merged = incoming
        .filter((m) => m.id != null)
        .map((m) => {
            const prev = prevById.get(m.id);
            const next = { ...prev, ...m };

            // Локальные поля оптимистичной отправки не затираем ответом IS при поллинге.
            if (prev?.status) {
                next.status = prev.status;
            }

            if (prev?.localId) {
                next.localId = prev.localId;
            }

            if (prev?.isOwn) {
                next.isOwn = true;
            }

            return next;
        });

    // Время сообщений из IS — с точностью до минуты, поэтому при равном времени
    // сортируем по id (он монотонный) — иначе сообщения одной минуты прыгают местами.
    const sortKey = (m) => m.time || m.createdAt || 0;

    return [...merged, ...pending].sort((a, b) => {
        const byTime = sortKey(a) - sortKey(b);

        return byTime !== 0 ? byTime : (a.id || 0) - (b.id || 0);
    });
};

export default (state = initialState, action) => {
    switch (action.type) {
        case UPDATE_MESSENGER_CHATS: {
            const incoming = action.chats || [];
            const chats = action.replace
                ? [...incoming].sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0))
                : upsertById(state.chats, incoming);

            if (!action.replace) {
                return {
                    ...state,
                    chats,
                    syncTime: action.syncTime || state.syncTime,
                };
            }

            const validKeys = new Set(chats.map(chatKey));
            const messagesByChat = Object.fromEntries(Object.entries(state.messagesByChat).filter(([key]) => validKeys.has(key)));

            let activeChat = state.activeChat;

            if (activeChat && !validKeys.has(chatKey(activeChat))) {
                const samePersonal =
                    activeChat.type === 'personal'
                        ? chats.find((chat) => chat.type === 'personal' && chat.title === activeChat.title)
                        : null;

                activeChat = samePersonal || null;
            }

            return {
                ...state,
                chats,
                messagesByChat,
                activeChat,
                syncTime: action.syncTime || state.syncTime,
            };
        }

        case UPDATE_MESSENGER_THREAD: {
            const key = chatKey(action.chat);

            return {
                ...state,
                messagesByChat: {
                    ...state.messagesByChat,
                    [key]: mergeMessages(state.messagesByChat[key], action.messages || []),
                },
                threadSyncTime: {
                    ...state.threadSyncTime,
                    [key]: action.syncTime || state.threadSyncTime[key],
                },
            };
        }

        case SET_MESSENGER_ACTIVE_CHAT:
            return { ...state, activeChat: action.chat };

        case SET_MESSENGER_SEARCH:
            return { ...state, search: action.search };

        case SET_MESSENGER_FLIGHT_SCOPE:
            return { ...state, flightScopePlanFlightId: action.planFlightId != null ? action.planFlightId : null };

        case SET_MESSENGER_STATUS:
            return { ...state, status: action.status };

        case UPDATE_MESSENGER_RECIPIENTS:
            return { ...state, recipients: action.recipients || [] };

        case ENQUEUE_MESSENGER_MESSAGE: {
            const { message } = action;
            const optimistic = {
                id: null,
                localId: message.localId,
                text: message.text,
                attachments: message.attachments || [],
                createdAt: message.createdAt,
                time: message.createdAt,
                isOwn: true,
                status: 'queued',
            };

            return {
                ...state,
                outbox: [...state.outbox, message],
                messagesByChat: {
                    ...state.messagesByChat,
                    [message.chatKey]: [...(state.messagesByChat[message.chatKey] || []), optimistic],
                },
            };
        }

        case RESOLVE_MESSENGER_MESSAGE: {
            const { localId, message, chatKey: key } = action;
            const thread = (state.messagesByChat[key] || []).map((m) =>
                m.localId === localId ? { ...m, ...(message || {}), localId, status: 'sent' } : m
            );

            return {
                ...state,
                outbox: state.outbox.filter((m) => m.localId !== localId),
                messagesByChat: { ...state.messagesByChat, [key]: thread },
            };
        }

        case FAIL_MESSENGER_MESSAGE: {
            const { localId, chatKey: key } = action;
            const thread = (state.messagesByChat[key] || []).map((m) => (m.localId === localId ? { ...m, status: 'failed' } : m));

            return {
                ...state,
                outbox: state.outbox.filter((m) => m.localId !== localId),
                messagesByChat: { ...state.messagesByChat, [key]: thread },
            };
        }

        case MARK_MESSENGER_CHAT_READ:
            return {
                ...state,
                chats: state.chats.map((chat) =>
                    chat.id === action.chatId && chat.type === action.chatType ? { ...chat, unreadCount: 0 } : chat
                ),
            };

        case LOGOUT:
            return initialState;

        default:
            return state;
    }
};
